import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { components } from '../../api/schema';
import { RichText } from '../../components/RichText';
import { request } from '../../lib/apiClient';
import { ApiError } from '../../lib/apiError';

export type Announcement = components['schemas']['AnnouncementRead'];

const TONE: Record<string, string> = {
  info: 'notice--info',
  warning: 'notice--warning',
  outage: 'notice--danger',
};

/**
 * Ankuendigungen und Stoerungsbanner -- F3-10, K-56.
 *
 * **Ganz oben und ohne Wegklicken.** Eine Stoerungsmeldung, die sich
 * schliessen laesst, ist beim zweiten Kollegen schon geschlossen -- und der
 * dritte ruft an, weil er nichts davon wusste. Sie verschwindet, wenn ihr
 * Zeitraum endet, und sonst nicht.
 *
 * **Eine Minute Vorhaltezeit.** Ein Banner, das erst beim naechsten
 * Seitenwechsel erscheint, kommt bei dem zu spaet, der gerade an einem Ticket
 * schreibt -- und genau der soll wissen, dass die Telefonanlage steht.
 */
export function AnnouncementBanner({ portal = false }: { portal?: boolean }) {
  const { t } = useTranslation();

  const announcements = useQuery({
    queryKey: ['announcements', 'active', portal],
    queryFn: () =>
      request<Announcement[]>('/announcements/active', { query: portal ? { portal: true } : {} }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isPermanent) && failureCount < 2,
  });

  if (!announcements.data || announcements.data.length === 0) {
    return null;
  }

  return (
    <div role="region" aria-label={t('announcements.title')}>
      {announcements.data.map((announcement) => (
        <div
          key={announcement.id}
          className={`notice ${TONE[announcement.level] ?? 'notice--info'}`}
          role={announcement.level === 'outage' ? 'alert' : 'status'}
        >
          <strong>{announcement.title}</strong>
          <RichText html={announcement.body} className="announcement__body" />
        </div>
      ))}
    </div>
  );
}
